import { AddressSuggestion, AutoSuggestAddressBackendApi, GetSuggestionsResponse } from './types';
import { toNswPointRawAddress } from './toNswPointRawAddress';
import { AutoSuggestAddressType, AutoSuggestAddressApi } from '../types';
import { useCallback, useRef, useState } from 'react';
import { useDebounce, useLatest } from 'react-use';
import axios from 'axios';

interface AddressSearchCallbacks {
  abortController: AbortController;
  resolve: (result: GetSuggestionsResponse) => void;
}

interface SearchParams {
  searchTerm: string;
  requestNo: number;
}

const DEFAULT_ADDRESS_ID = 'UseAddressAsEntered';

export const useAutoSuggestAddressBackendApi = (
  api: AutoSuggestAddressApi,
  addressType?: AutoSuggestAddressType,
  minSearchLength = 8,
  debounceMillis = 1000,
  defaultSuggestion: AddressSuggestion | null = null
): AutoSuggestAddressBackendApi => {
  const [searchParams, setSearchParams] = useState<SearchParams>();

  const apiRef = useLatest(api);
  const executorRef = useRef<AddressSearchCallbacks>();

  useDebounce(
    async () => {
      const callbacks = executorRef.current;
      if (!callbacks) return;

      try {
        const searchResults = await apiRef.current.getSuggestions(
          searchParams.searchTerm,
          addressType,
          callbacks.abortController.signal
        );

        const suggestions = searchResults
          // If there are no values, the id is something like 'UseAddressAsEntered_NoValues'.
          .filter((autocomplete) => !autocomplete.id.startsWith(DEFAULT_ADDRESS_ID))
          .map((autocomplete, index) => ({
            id: autocomplete.id,
            address: autocomplete.label,
            rank: index,
          }));

        callbacks.resolve({
          suggestions,
          requestNo: searchParams.requestNo,
        });
        executorRef.current = undefined;
      } catch (err) {
        // Ignore cancelled errors - this is just the abort signal doing it's thing.
        if (!axios.isCancel(err)) {
          throw err;
        }
      }
    },
    debounceMillis,
    [searchParams, addressType]
  );

  const getSuggestions = useCallback(
    (searchTerm: string, requestNo: number): Promise<GetSuggestionsResponse> => {
      if (executorRef.current?.abortController) {
        executorRef.current.abortController.abort();
      }

      if (searchTerm.length < minSearchLength) {
        // Return a custom suggestion informing the user that they need to enter more characters before the autocomplete
        // will trigger a search.
        return Promise.resolve({
          suggestions: [defaultSuggestion],
          requestNo,
        });
      }

      setSearchParams({ searchTerm, requestNo });

      return new Promise((resolve) => {
        executorRef.current = {
          abortController: new AbortController(),
          resolve,
        };
      });
    },
    [minSearchLength, defaultSuggestion]
  );

  const getDetailsById = useCallback(async (id: string) => {
    const response = await apiRef.current.getDetailsById(id);

    // The SNSW AutoSuggestAddress still runs its own NSW Point converter, which expects rawAddress.data.addressDetails.
    // The v3 API no longer supplies rawAddress, so rebuild that wrapper from the new address payload before returning it.
    return toNswPointRawAddress(response.address);
  }, []);

  return { getSuggestions, getDetailsById };
};
